"use client";

import { useState, useTransition, useEffect, useRef } from "react";
import Image from "next/image";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth } from "@/lib/firebase";
const googleProvider = new GoogleAuthProvider();
import { FloatingInput } from "@/components/ui/floating-input";
import { PasswordInput } from "@/components/ui/password-input";
import { signIn } from "./actions";
import { requestMagicLink, requestPasswordReset, requestEmailVerification } from "@/lib/magic-link";
import { RecaptchaCheckbox, type RecaptchaCheckboxHandle } from "@/components/captcha/recaptcha-checkbox";
import { RecaptchaDisclosure } from "@/components/captcha/recaptcha-disclosure";
import { reportError } from "@/lib/report-error";
import { getSessionId } from "@/lib/session-id";
import { safeNextPath } from "@/lib/login-next";
// Format-only on purpose: login references an EXISTING account, so the
// strict TLD/typo rules must never lock a registered address out.
import { isEmailFormatValid as validateEmailFormat } from "@/lib/email-validation";

/** After sign-in, the page the visitor opened before /login sent them here
 *  (`?next=`), or the role's usual landing page. */
function afterSignIn(defaultRedirect: string): string {
  return safeNextPath(new URLSearchParams(window.location.search).get("next")) ?? defaultRedirect;
}
function mapFirebaseError(code: string): string {
  switch (code) {
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-credential":
      return "Invalid email or password";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    default:
      return "An error occurred. Please try again.";
  }
}
export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetNoAccount, setResetNoAccount] = useState(false);
  const [resetCaptchaToken, setResetCaptchaToken] = useState<string | null>(null);
  const resetCaptchaRef = useRef<RecaptchaCheckboxHandle>(null);
  const [magicMode, setMagicMode] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<RecaptchaCheckboxHandle>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [cooldownSecondsLeft, setCooldownSecondsLeft] = useState(0);
  useEffect(() => {
    if (cooldownUntil === null) return;
    const tick = () => {
      const left = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownSecondsLeft(left);
      if (left === 0) setCooldownUntil(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);
  const [isPending, startTransition] = useTransition();
  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches) {
      emailRef.current?.focus();
    }
  }, []);
  const isReady = email.trim() !== "" && password.trim() !== "";
  function handleSignIn() {
    setError("");
    setShowResend(false);
    setResendSent(false);
    startTransition(async () => {
      try {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        if (!cred.user.emailVerified) {
          setError("Please verify your email address before signing in.");
          setShowResend(true);
          return;
        }
        const idToken = await cred.user.getIdToken();
        const result = await signIn(idToken, undefined, {
          sessionId: getSessionId()
        });
        if (result?.error) {
          setError(result.error);
        } else if (result?.redirect) {
          window.location.href = afterSignIn(result.redirect);
        }
      } catch (err: unknown) {
        const code = (err as {
          code?: string;
        })?.code ?? "";
        reportError({
          kind: "auth",
          message: code || "signInWithEmailAndPassword failed",
          error_code: code,
          context: {
            provider: "firebase",
            flow: "password_login"
          }
        });
        setError(mapFirebaseError(code));
      }
    });
  }
  async function handleResendVerification() {
    if (!auth.currentUser) return;
    try {
      const idToken_0 = await auth.currentUser.getIdToken();
      const result_0 = await requestEmailVerification(idToken_0);
      if (result_0.ok) {
        setResendSent(true);
      } else if (result_0.reason === "throttled") {
        setCooldownUntil(Date.now() + result_0.retryAfterSec * 1000);
      } else {
        reportError({
          kind: "api_error",
          message: "resend verification email failed",
          error_code: result_0.reason,
          context: {
            flow: "resend_verification"
          }
        });
        setError("Failed to resend verification email. Please try again.");
      }
    } catch (err_0: unknown) {
      reportError({
        kind: "network",
        message: err_0 instanceof Error ? err_0.message : "resend verification threw",
        stack: err_0 instanceof Error ? err_0.stack : undefined,
        context: {
          flow: "resend_verification"
        }
      });
      setError("Failed to resend verification email. Please try again.");
    }
  }
  async function handleForgotPassword() {
    setResetError("");
    setResetNoAccount(false);
    if (!resetEmail.trim()) {
      setResetError("Please enter your email address");
      return;
    }
    if (!resetCaptchaToken) {
      setResetError("Please complete the security check.");
      return;
    }
    if (cooldownUntil !== null) return;
    startTransition(async () => {
      const result_1 = await requestPasswordReset({
        email: resetEmail.trim(),
        token: resetCaptchaToken
      });
      if (result_1.ok) {
        setResetSent(true);
        return;
      }
      resetCaptchaRef.current?.reset();
      setResetCaptchaToken(null);
      if (result_1.reason === "throttled") {
        setCooldownUntil(Date.now() + result_1.retryAfterSec * 1000);
        setResetError("");
      } else if (result_1.reason === "captcha") {
        reportError({
          kind: "auth",
          severity: "warning",
          message: "password reset captcha verification failed",
          error_code: "captcha",
          context: {
            flow: "password_reset"
          }
        });
        setResetError("Verification failed. Please try again.");
      } else if (result_1.reason === "no-account") {
        setResetNoAccount(true);
      } else if (result_1.reason === "send-failed") {
        reportError({
          kind: "api_error",
          message: "password reset send failed",
          error_code: "send-failed",
          context: {
            flow: "password_reset"
          }
        });
        setResetError("We couldn't send the reset email. Please try again in a moment.");
      } else {
        reportError({
          kind: "network",
          message: "password reset unreachable",
          error_code: result_1.reason,
          context: {
            flow: "password_reset"
          }
        });
        setResetError("Could not reach the server. Please try again.");
      }
    });
  }
  function handleGoogleSignIn() {
    setError("");
    setShowResend(false);
    startTransition(async () => {
      try {
        const cred_0 = await signInWithPopup(auth, googleProvider);
        const idToken_1 = await cred_0.user.getIdToken();
        const result_2 = await signIn(idToken_1, cred_0.user.displayName, {
          sessionId: getSessionId()
        });
        if (result_2?.error) {
          setError(result_2.error);
        } else if (result_2?.redirect) {
          window.location.href = afterSignIn(result_2.redirect);
        }
      } catch (err_1: unknown) {
        const code_0 = (err_1 as {
          code?: string;
        })?.code ?? "";
        if (code_0 === "auth/popup-closed-by-user") return;
        if (code_0 === "auth/popup-blocked") {
          reportError({
            kind: "auth",
            severity: "warning",
            message: code_0,
            error_code: code_0,
            context: {
              provider: "google",
              flow: "popup_login"
            }
          });
          setError("Popup was blocked. Please allow popups for this site.");
          return;
        }
        reportError({
          kind: "auth",
          message: code_0 || "signInWithPopup failed",
          error_code: code_0,
          stack: err_1 instanceof Error ? err_1.stack : undefined,
          context: {
            provider: "google",
            flow: "popup_login"
          }
        });
        console.error("[Google Sign-In] error:", code_0, err_1);
        setError(`Google sign-in failed: ${code_0 || "unknown error"}`);
      }
    });
  }
  async function handleSendMagicLink() {
    setMagicError("");
    if (!validateEmailFormat(magicEmail.trim())) {
      setMagicError("Please enter a valid email address");
      return;
    }
    if (!captchaToken) {
      setMagicError("Please complete the security check.");
      return;
    }
    if (cooldownUntil !== null) return;
    startTransition(async () => {
      const result_3 = await requestMagicLink({
        email: magicEmail.trim(),
        token: captchaToken,
        mode: "login"
      });
      if (result_3.ok) {
        setMagicSent(true);
        return;
      }
      captchaRef.current?.reset();
      setCaptchaToken(null);
      if (result_3.reason === "throttled") {
        setCooldownUntil(Date.now() + result_3.retryAfterSec * 1000);
        setMagicError("");
      } else if (result_3.reason === "captcha") {
        reportError({
          kind: "auth",
          severity: "warning",
          message: "magic-link captcha verification failed",
          error_code: "captcha",
          context: {
            flow: "magic_link",
            mode: "login"
          }
        });
        setMagicError("Verification failed. Please try again.");
      } else if (result_3.reason === "send-failed") {
        reportError({
          kind: "api_error",
          message: "magic-link send failed",
          error_code: "send-failed",
          context: {
            flow: "magic_link",
            mode: "login"
          }
        });
        setMagicError("We couldn't send the sign-in link. Please try again in a moment.");
      } else {
        reportError({
          kind: "network",
          message: "magic-link unreachable",
          error_code: result_3.reason,
          context: {
            flow: "magic_link",
            mode: "login"
          }
        });
        setMagicError("Could not reach the server. Please try again.");
      }
    });
  }
  function formatCooldown(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }
  if (magicMode) {
    return <div className="w-[460px] max-w-[95%] bg-white rounded-[var(--radius-4xl)] shadow-[var(--shadow-modal)] overflow-hidden">
        <div className="bg-[var(--color-primary)] px-8 py-8 text-center">
          <Image src="/logos/sl-logo.webp" alt="State & Liberty" width={200} height={56} className="h-14 mx-auto object-contain" />
          <p className="text-[0.65rem] uppercase tracking-[4px] text-[#ccc] mt-1">Wedding Details Form</p>
        </div>
        <div className="px-10 py-8">
          {magicSent ? <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--color-success-light)] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2"><path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" /></svg>
              </div>
              <h2 className="text-[1.25rem] font-semibold mb-2">Check Your Email</h2>
              <p className="text-[0.9rem] text-[var(--color-gray-500)] mb-6 leading-relaxed">
                We sent a sign-in link to <strong className="text-[var(--color-gray-700)]">{magicEmail}</strong>. Click the link to finish signing in.
              </p>
              <button onClick={() => {
            setMagicMode(false);
            setMagicSent(false);
            setMagicEmail("");
          }} className="w-full py-3.5 text-base font-bold rounded-[var(--radius-lg)] bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-colors cursor-pointer">
                Back to Sign In
              </button>
            </div> : <>
              <h2 className="text-[1.25rem] font-semibold text-center mb-2">Sign in with Email Link</h2>
              <p className="text-[0.875rem] text-[var(--color-gray-500)] text-center mb-6">
                We&apos;ll email you a one-click sign-in link.
              </p>

              {magicError && <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-[var(--color-danger-light)] rounded-[var(--radius-lg)]">
                  <span className="text-[0.85rem] text-[var(--color-danger)]">{magicError}</span>
                </div>}

              <form onSubmit={e => {
            e.preventDefault();
            if (!isPending) handleSendMagicLink();
          }}>
                <FloatingInput label="Email" type="email" value={magicEmail} onChange={e_0 => setMagicEmail(e_0.target.value)} />

                <div className="mt-5">
                  <RecaptchaCheckbox ref={captchaRef} onToken={setCaptchaToken} />
                </div>

                {cooldownUntil !== null && cooldownSecondsLeft > 0 && <p className="mt-3 text-center text-[0.85rem] text-[var(--color-danger)]">
                    You&apos;ve sent 3 sign-in links recently. Try again in {formatCooldown(cooldownSecondsLeft)}.
                  </p>}

                <div className="flex flex-col gap-3 mt-5">
                  <button type="submit" disabled={!validateEmailFormat(magicEmail.trim()) || !captchaToken || isPending || cooldownUntil !== null} className={`w-full py-3.5 text-base font-bold rounded-[var(--radius-lg)] cursor-pointer disabled:cursor-not-allowed transition-colors ${validateEmailFormat(magicEmail.trim()) && captchaToken && cooldownUntil === null ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]" : "bg-[var(--color-gray-400)] text-white"}`}>
                    {isPending ? "Sending..." : "Send Sign-In Link"}
                  </button>
                  <RecaptchaDisclosure />
                  <button type="button" onClick={() => {
                setMagicMode(false);
                setMagicError("");
                setCaptchaToken(null);
                captchaRef.current?.reset();
              }} className="w-full py-2 text-[0.9rem] text-[var(--color-gray-500)] hover:text-[var(--color-gray-700)] cursor-pointer">
                    Back to Sign In
                  </button>
                </div>
              </form>
            </>}
        </div>
      </div>;
  }
  if (forgotMode) {
    return <div className="w-[460px] max-w-[95%] bg-white rounded-[var(--radius-4xl)] shadow-[var(--shadow-modal)] overflow-hidden">
        <div className="bg-[var(--color-primary)] px-8 py-8 text-center">
          <Image src="/logos/sl-logo.webp" alt="State & Liberty" width={200} height={56} className="h-14 mx-auto object-contain" />
          <p className="text-[0.65rem] uppercase tracking-[4px] text-[#ccc] mt-1">Wedding Details Form</p>
        </div>
        <div className="px-10 py-8">
          {resetSent ? <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--color-success-light)] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2"><path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" /></svg>
              </div>
              <h2 className="text-[1.25rem] font-semibold mb-2">Check Your Email</h2>
              <p className="text-[0.9rem] text-[var(--color-gray-500)] mb-6 leading-relaxed">
                We sent a password reset link to <strong className="text-[var(--color-gray-700)]">{resetEmail}</strong>
              </p>
              <button onClick={() => {
            setForgotMode(false);
            setResetSent(false);
          }} className="w-full py-3.5 text-base font-bold rounded-[var(--radius-lg)] bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-colors cursor-pointer">
                Back to Sign In
              </button>
            </div> : <>
              <h2 className="text-[1.25rem] font-semibold text-center mb-2">Reset Password</h2>
              <p className="text-[0.875rem] text-[var(--color-gray-500)] text-center mb-6">
                Enter your email and we'll send you a reset link.
              </p>

              {resetError && <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-[var(--color-danger-light)] rounded-[var(--radius-lg)]">
                  <span className="text-[0.85rem] text-[var(--color-danger)]">{resetError}</span>
                </div>}

              {resetNoAccount && <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-[var(--color-danger-light)] rounded-[var(--radius-lg)]">
                  <span className="text-[0.85rem] text-[var(--color-danger)]">
                    We couldn&apos;t find an account associated with this email address. Please verify the address is correct, or{" "}
                    <a href="/register" className="font-medium underline">register for a new account</a>.
                  </span>
                </div>}

              <FloatingInput label="Email" type="email" value={resetEmail} onChange={e_1 => setResetEmail(e_1.target.value)} />

              <div className="mt-5">
                <RecaptchaCheckbox ref={resetCaptchaRef} onToken={setResetCaptchaToken} />
              </div>

              {cooldownUntil !== null && cooldownSecondsLeft > 0 && <p className="mt-3 text-center text-[0.85rem] text-[var(--color-danger)]">
                  You&apos;ve sent 3 reset emails recently. Try again in {formatCooldown(cooldownSecondsLeft)}.
                </p>}

              <div className="flex flex-col gap-3 mt-6">
                <button onClick={handleForgotPassword} disabled={!resetEmail.trim() || !resetCaptchaToken || isPending || cooldownUntil !== null} className={`w-full py-3.5 text-base font-bold rounded-[var(--radius-lg)] cursor-pointer disabled:cursor-not-allowed transition-colors ${resetEmail.trim() && resetCaptchaToken && cooldownUntil === null ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]" : "bg-[var(--color-gray-400)] text-white"}`}>
                  {isPending ? "Sending..." : "Send Reset Link"}
                </button>
                <RecaptchaDisclosure />
                <button onClick={() => {
              setForgotMode(false);
              setResetError("");
              setResetNoAccount(false);
              setResetCaptchaToken(null);
              resetCaptchaRef.current?.reset();
            }} className="w-full py-2 text-[0.9rem] text-[var(--color-gray-500)] hover:text-[var(--color-gray-700)] cursor-pointer">
                  Back to Sign In
                </button>
              </div>
            </>}
        </div>
      </div>;
  }
  return <div className="w-[460px] max-w-[95%] bg-white rounded-[var(--radius-4xl)] shadow-[var(--shadow-modal)] overflow-hidden">
      <div className="bg-[var(--color-primary)] px-8 py-8 text-center">
        <Image src="/logos/sl-logo.webp" alt="State & Liberty" width={200} height={56} className="h-14 mx-auto object-contain" />
        <p className="text-[0.65rem] uppercase tracking-[4px] text-[#ccc] mt-1">Wedding Details Form</p>
      </div>

      <div className="px-10 py-8">
        <h2 className="text-[1.25rem] font-semibold text-center mb-1">Sign in to your account</h2>
        <p className="text-[0.875rem] text-[var(--color-gray-500)] text-center mb-6">
          No account yet?{" "}
          <a href="/register" className="text-[var(--color-accent)] font-medium hover:underline">Register here</a>
        </p>

        {error && <div className="px-4 py-3 mb-4 bg-[var(--color-danger-light)] rounded-[var(--radius-lg)] animate-[fadeIn_0.3s_ease]">
            <div className="flex items-center gap-2">
              <span className="flex-shrink-0 w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[var(--color-danger)]">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </span>
              <span className="text-[0.85rem] text-[var(--color-danger)]">{error}</span>
            </div>
            {showResend && <div className="mt-2 ml-[26px]">
                {resendSent ? <span className="text-[0.8rem] text-[var(--color-success)]">Verification email sent!</span> : <button type="button" onClick={handleResendVerification} className="text-[0.8rem] text-[var(--color-accent)] font-medium hover:underline cursor-pointer">
                    Resend Verification Email
                  </button>}
              </div>}
          </div>}

        <form onSubmit={e_2 => {
        e_2.preventDefault();
        if (!isReady || isPending) return;
        handleSignIn();
      }}>
          <div className="flex flex-col gap-4">
            <FloatingInput ref={emailRef} label="Email" type="email" value={email} onChange={e_3 => setEmail(e_3.target.value)} />
            <div>
              <PasswordInput label="Password" value={password} onChange={e_4 => setPassword(e_4.target.value)} />
              <div className="flex justify-end mt-1">
                <button type="button" onClick={() => {
                setForgotMode(true);
                setResetEmail(email);
                setResetSent(false);
                setResetError("");
                setResetNoAccount(false);
              }} className="text-[0.8rem] text-[var(--color-accent)] hover:underline cursor-pointer">
                  Forgot password?
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button type="submit" disabled={!isReady || isPending} className={`w-full py-3.5 text-base font-bold rounded-[var(--radius-lg)] cursor-pointer disabled:cursor-not-allowed transition-colors ${isReady ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]" : "bg-[var(--color-gray-400)] text-white"}`}>
              {isPending ? <span className="inline-flex items-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Signing in...</span> : "Sign In"}
            </button>
          </div>
        </form>

        <div className="mt-0">
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[var(--color-gray-200)]" />
            <span className="text-[0.8rem] text-[var(--color-gray-400)]">or</span>
            <div className="flex-1 h-px bg-[var(--color-gray-200)]" />
          </div>

          <button type="button" onClick={handleGoogleSignIn} disabled={isPending} className="w-full py-3 text-[0.95rem] font-medium rounded-[var(--radius-lg)] border border-[var(--color-gray-300)] bg-white hover:bg-[var(--color-gray-50)] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>

          <button type="button" onClick={() => {
          setMagicMode(true);
          setMagicEmail(email);
          setMagicSent(false);
          setMagicError("");
        }} disabled={isPending} className="mt-3 w-full py-3 text-[0.95rem] font-medium rounded-[var(--radius-lg)] border border-[var(--color-gray-300)] bg-white hover:bg-[var(--color-gray-50)] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
            Continue with Email Link
          </button>
        </div>
      </div>
    </div>;
}